import { Action, Store } from '@ngrx/store';
import { FunctionIsNotAllowed } from '@ngrx/store/src/models';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, first, map, pluck } from 'rxjs/operators';
import { Reducer, StoreService, TS } from './store.types';

/**
 * This is a separate class so that the existing RxStore can be used
 * in the webpack 3 build seamlessly.
 */
export class GenericLegacyRxStoreService<ST, A extends Action> implements StoreService<ST, A> {
  constructor(private store: Store<ST>) {}

  addFeature<STNew>(name: string, reducer: Reducer<STNew, A>): StoreService<STNew & ST, A> {
    return null; // not needed in ngrx-world
  }

  select<K>(mapFn: (state: ST) => K): Observable<K>;
  select<a extends keyof ST>(key: a): Observable<ST[a]>;
  select<a extends keyof ST, b extends keyof ST[a]>(key1: a, key2: b): Observable<ST[a][b]>;
  select<a extends keyof ST, b extends keyof ST[a], c extends keyof ST[a][b]>(
    key1: a,
    key2: b,
    key3: c
  ): Observable<ST[a][b][c]>;
  select<a extends keyof ST, b extends keyof ST[a], c extends keyof ST[a][b], d extends keyof ST[a][b][c]>(
    key1: a,
    key2: b,
    key3: c,
    key4: d
  ): Observable<ST[a][b][c][d]>;
  select<
    a extends keyof ST,
    b extends keyof ST[a],
    c extends keyof ST[a][b],
    d extends keyof ST[a][b][c],
    e extends keyof ST[a][b][c][d]
  >(key1: a, key2: b, key3: c, key4: d, key5: e): Observable<ST[a][b][c][d][e]>;
  select<
    a extends keyof ST,
    b extends keyof ST[a],
    c extends keyof ST[a][b],
    d extends keyof ST[a][b][c],
    e extends keyof ST[a][b][c][d],
    f extends keyof ST[a][b][c][d][e]
  >(key1: a, key2: b, key3: c, key4: d, key5: e, key6: f): Observable<ST[a][b][c][d][e][f]>;
  select(pathOrMapFn: ((state: ST) => any) | string, ...paths: string[]): Observable<any> {
    let mapped$: Observable<any>;

    if (typeof pathOrMapFn === 'string') {
      mapped$ = this.store.select((state) => state).pipe(pluck(pathOrMapFn, ...paths));
    } else if (typeof pathOrMapFn === 'function') {
      mapped$ = this.store.select((state) => state).pipe(map(pathOrMapFn));
    } else {
      throw new TypeError(
        `Unexpected type '${typeof pathOrMapFn}' in select operator,` + ` expected 'string' or 'function'`
      );
    }

    return mapped$.pipe(distinctUntilChanged());
  }

  selectNoEmpty<K>(mapFn: (state: ST) => K): Observable<K> {
    return this.select(mapFn).pipe(filter((v) => v != null));
  }

  selectWithParam<P, R>(selector: (state: ST, param: P) => R, param$: Observable<P>): Observable<R> {
    return combineLatest([this.store.select((state) => state), param$.pipe(filter((p) => p != null))]).pipe(
      map(([state, param]) => selector(state, param)),
      distinctUntilChanged()
    );
  }

  dispatch(
    action: A &
      FunctionIsNotAllowed<
        A,
        'Functions are not allowed to be dispatched. Did you forget to call action creator function?'
      >
  ): void {
    // console.log(action);
    this.store.dispatch(action as any);
  }

  dispatchThunk<ST, K1>(s1: TS<ST, K1>, actionMapper: (r1: K1) => A);
  dispatchThunk<ST, K1, K2>(s1: TS<ST, K1>, s2: TS<ST, K2>, actionMapper: (r1: K1, r2: K2) => A);
  dispatchThunk<ST, K1, K2, K3>(
    s1: TS<ST, K1>,
    s2: TS<ST, K2>,
    s3: TS<ST, K3>,
    actionMapper: (r1: K1, r2: K2, r3: K3) => A
  );
  dispatchThunk<ST, K1, K2, K3, K4>(
    s1: TS<ST, K1>,
    s2: TS<ST, K2>,
    s3: TS<ST, K3>,
    s4: TS<ST, K4>,
    actionMapper: (r1: K1, r2: K2, r3: K3, r4: K4) => A
  );
  dispatchThunk(...args: Function[]) {
    combineLatest(args.slice(0, args.length - 1).map((arg) => this.store.select(arg as any)))
      .pipe(first())
      .subscribe((res) => {
        this.dispatch(args[args.length - 1].apply(null, res));
      });
  }

  dispatchThunkPipe<K, T>(
    selector: (state: ST) => K,
    pipeFn: (obs: Observable<K>) => Observable<T>,
    actionMapper: (res: T) => A
  ): void {
    pipeFn(this.store.select(selector))
      .pipe(
        first() // This side-effect should only last for this single dispatch call
      )
      .subscribe((res) => {
        this.dispatch(actionMapper(res) as any);
      });
  }
}
